import {ErrorCode, Permission} from '@sollapay/enums';
import {AVATAR_ALLOWED_MIME_TYPES, AVATAR_MAX_BYTES} from '@sollapay/utils';
import {usePendingImageUpload} from '@sollapay/web-core';
import {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {showResultToastCard} from '@/components';
import {usePermissions} from '@/features/authentication';
import {useApiClient} from '@/hooks';
import {getErrorCode, getTranslatedErrorMessage} from '@/utils';

import {firmSettingsApi} from '../api';
import {useUpdateFirmProfileMutation} from './useUpdateFirmProfileMutation';

import type {FirmProfileDTO} from '@sollapay/types';

export interface FirmSettingsFormValues {
  displayName: string;
}

function mapProfileToFormValues(profile?: FirmProfileDTO): FirmSettingsFormValues {
  return {displayName: profile?.displayName ?? ''};
}

export interface UseFirmSettingsFormParams {
  isOpen: boolean;
  profile?: FirmProfileDTO;
  onSuccess: () => void;
}

export function useFirmSettingsForm({isOpen, profile, onSuccess}: UseFirmSettingsFormParams) {
  const {t} = useTranslation(['settings', 'common']);
  const client = useApiClient();
  const {hasPermission} = usePermissions();
  const canEdit = hasPermission(Permission.FIRM_SETTINGS_UPDATE);
  const updateProfileMutation = useUpdateFirmProfileMutation();

  const form = useForm<FirmSettingsFormValues>({
    defaultValues: {displayName: ''},
    mode: 'onTouched'
  });

  const logoUpload = usePendingImageUpload({
    maxBytes: AVATAR_MAX_BYTES,
    allowedMimeTypes: AVATAR_ALLOWED_MIME_TYPES,
    currentUrl: profile?.logoUrl ?? undefined,
    tooLargeMessage: t(`common:errors.${ErrorCode.LOGO_TOO_LARGE}`),
    unsupportedTypeMessage: t(`common:errors.${ErrorCode.UNSUPPORTED_LOGO_TYPE}`)
  });

  // Covers the S3 PUT phase too, same as useAccountSettingsForm's isUploadingAvatar — the
  // mutation's own isPending only starts once the PATCH call begins, after the upload finishes.
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  // Same reset-on-open trap as useAccountSettingsForm — Modal stays mounted across open/close.
  useEffect(() => {
    if (isOpen) {
      form.reset(mapProfileToFormValues(profile));
      logoUpload.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, profile, form]);

  const {
    formState: {isDirty}
  } = form;

  const handleSave = form.handleSubmit(async values => {
    try {
      let logoKey: string | null | undefined;

      if (logoUpload.pendingFile) {
        setIsUploadingLogo(true);
        const {data: uploadUrlDto} = await firmSettingsApi.createLogoUploadUrl(client, {
          fileName: logoUpload.pendingFile.name,
          contentType: logoUpload.pendingFile.type,
          fileSize: logoUpload.pendingFile.size
        });
        await firmSettingsApi.uploadLogoToS3(uploadUrlDto.uploadUrl, logoUpload.pendingFile);
        logoKey = uploadUrlDto.key;
      } else if (logoUpload.isRemoved) {
        logoKey = null;
      }

      await updateProfileMutation.mutateAsync({
        displayName: values.displayName.trim(),
        ...(logoKey !== undefined ? {logoKey} : {})
      });

      form.reset(values);
      logoUpload.reset();
      showResultToastCard({
        isError: false,
        title: t('firmModal.resultToast.success.title'),
        description: t('firmModal.resultToast.success.description'),
        closeLabel: t('firmModal.resultToast.close')
      });
      onSuccess();
    } catch (error) {
      const code = getErrorCode(error);
      if (code === ErrorCode.LOGO_TOO_LARGE || code === ErrorCode.UNSUPPORTED_LOGO_TYPE) {
        logoUpload.setError(getTranslatedErrorMessage(error, t));
      } else {
        showResultToastCard({
          isError: true,
          title: t('firmModal.resultToast.error.title'),
          description: getTranslatedErrorMessage(error, t),
          closeLabel: t('firmModal.resultToast.close')
        });
      }
    } finally {
      setIsUploadingLogo(false);
    }
  });

  return {
    form,
    handleSave,
    canEdit,
    isSaving: isUploadingLogo || updateProfileMutation.isPending,
    logoUrl: logoUpload.previewUrl,
    logoError: logoUpload.error,
    hasLogoChange: logoUpload.hasChange,
    onLogoFileSelect: logoUpload.onFileSelect,
    onRemoveLogo: logoUpload.onRemove,
    canSave: isDirty || logoUpload.hasChange
  };
}
